import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import { catalogRouteOptions, machineRouteId } from './machineRouteOptions';

export default function AssignRouteDialog({ machines, initialRoute, initialRouteId, onClose, onAssigned, preferredLanguage }) {
    const dispatch = useDispatch();
    const [source, setSource] = useState(initialRouteId || !initialRoute ? 'catalog' : 'legacy');
    const [route, setRoute] = useState(initialRouteId || initialRoute || null);
    const [user, setUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const submitting = useRef(false);
    const es = preferredLanguage !== 'en';
    const routes = useMemo(() => source === 'catalog' ? catalogRouteOptions(machines.filter(m => !m.isDeleted)) : [...new Set(machines.filter(m => !m.isDeleted).map(m => String(m.legacyRoute ?? m.route ?? '').trim()).filter(Boolean))].sort().map(value => ({ label: value, value })), [machines, source]);
    const count = machines.filter(m => !m.isDeleted && (source === 'catalog' ? machineRouteId(m) : String(m.legacyRoute ?? m.route ?? '').trim()) === route).length;

    useEffect(() => {
        let mounted = true;
        handleGetRequest(endPoints.getallUsers, true, false, dispatch).then(response => {
            if (!mounted) return;
            if (!Array.isArray(response?.data)) {
                setError(es ? 'No se pudieron cargar los usuarios. Cierra y vuelve a intentar.' : 'Could not load users. Close and try again.');
                return;
            }
            setUsers(response.data.filter(u => u.isActive !== false && !u.isDeleted).map(u => ({
                label: `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.email ? ` (${u.email})` : ''), value: u._id
            })));
        }).catch(() => {
            if (mounted) setError(es ? 'No se pudieron cargar los usuarios.' : 'Could not load users.');
        }).finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [dispatch, es]);

    const save = async () => {
        if (!route || !user || !count || submitting.current) return;
        submitting.current = true;
        setSaving(true);
        setError('');
        try {
            const response = await dispatch(handlePutRequest({ ...(source === 'catalog' ? { routeId: route } : { route }), user }, endPoints.assignMachineRoute, false, true));
            if (response?.status === 200) {
                onAssigned();
            } else {
                setError(response?.data?.message || (es ? 'No se pudo completar la asignación.' : 'Could not complete assignment.'));
            }
        } catch (err) {
            setError(es ? 'No se pudo completar la asignación.' : 'Could not complete assignment.');
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    return <Dialog header={es ? 'Asignar por ruta' : 'Assign by route'} visible onHide={() => !saving && onClose()}
        closable={!saving} closeOnEscape={!saving} draggable={false} style={{ width: '32rem', maxWidth: '95vw' }}
        footer={<><Button label={es ? 'Cancelar' : 'Cancel'} className="p-button-text" disabled={saving} onClick={onClose} />
            <Button label={es ? 'Confirmar asignación' : 'Confirm assignment'} disabled={!route || !user || !count || loading || saving} loading={saving} onClick={save} /></>}>
        <label htmlFor="assign-route-source" className="block mb-2">{es ? 'Origen de la ruta' : 'Route source'}</label>
        <Dropdown inputId="assign-route-source" value={source} options={[
            { label: es ? 'Ruta de la BD' : 'Database route', value: 'catalog' },
            { label: es ? 'Ruta heredada' : 'Legacy route', value: 'legacy' }
        ]} onChange={e => { setSource(e.value); setRoute(null); }} disabled={saving} className="w-full mb-3" />
        <label htmlFor="assign-route" className="block mb-2">{es ? 'Ruta' : 'Route'}</label>
        <Dropdown inputId="assign-route" value={route} options={routes} onChange={e => setRoute(e.value)} filter className="w-full mb-3" disabled={saving} placeholder={es ? 'Selecciona una ruta' : 'Select a route'} />
        <label htmlFor="assign-route-user" className="block mb-2">{es ? 'Responsable' : 'Assigned user'}</label>
        <Dropdown inputId="assign-route-user" value={user} options={users} onChange={e => setUser(e.value)} filter className="w-full" disabled={loading || saving} placeholder={es ? 'Selecciona un usuario' : 'Select a user'} />
        {route && <p>{es ? `${count} máquinas activas en ${routes.find(option => option.value === route)?.label || route}. Se incluyen todas las máquinas de la ruta, aunque otros filtros las oculten.` : `${count} active machines in ${routes.find(option => option.value === route)?.label || route}. Includes all machines in this route, even if hidden by other filters.`}</p>}
        <p>{es ? 'Cada máquina tiene un solo responsable. Al confirmar se reemplazará al responsable actual en todas las máquinas activas de esta ruta. Las máquinas que se agreguen después requerirán otra asignación.' : 'Each machine has one assigned user. Confirming replaces the current user on every active machine in this route. Machines added later require another assignment.'}</p>
        {error && <p role="alert" className="p-error">{error}</p>}
    </Dialog>;
}
